// Centralised service catalogue — used by Services card grid and service detail pages.
import {
  Hammer,
  Droplets,
  Zap,
  ShieldCheck,
  Trees,
  CalendarClock,
  Siren,
  FileCheck,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "building-maintenance",
    icon: Hammer,
    title: "General Building Maintenance",
    short: "Structural checks, repairs, painting, waterproofing — keeping every façade and fixture impeccable.",
    image:
      "https://images.unsplash.com/photo-1774271101773-8e7cacedf12b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwcm9wZXJ0eSUyMG1haW50ZW5hbmNlfGVufDB8fHx8MTc3OTM2MDE4Nnww&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-2",
    intro:
      "From structural integrity to the finest finishing touches — Greenroute's building maintenance protects the most visible asset of your estate. Our certified teams handle scheduled inspections, repairs, painting and waterproofing with a craftsperson's eye.",
    deliverables: [
      "Routine structural integrity inspections",
      "Internal & external painting and finishes",
      "Waterproofing of roofs, balconies, and basements",
      "Crack repair, sealant work and pointing",
      "Repairs to doors, locks, hinges & general carpentry",
      "Tile, grout and bathroom refurbishments",
    ],
    ideal_for: "Body corporates, estate trustees, and managing agents responsible for keeping common-area assets pristine.",
  },
  {
    slug: "plumbing-drainage",
    icon: Droplets,
    title: "Plumbing & Drainage",
    short: "Leak detection, pipe repairs, drain clearing, geyser servicing.",
    image:
      "https://images.pexels.com/photos/7031600/pexels-photo-7031600.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    intro:
      "Burst pipes don't keep office hours. Greenroute's plumbing teams diagnose and resolve leaks, blockages and geyser issues quickly — protecting your property from water damage and your residents from disruption.",
    deliverables: [
      "Acoustic & thermal leak detection",
      "Pipe repairs and replacements (copper, PVC, PEX)",
      "Blocked drain clearing & high-pressure jetting",
      "Geyser servicing, replacement and valve checks",
      "Storm-water management & gulley maintenance",
      "Pressure regulation and PRV installations",
    ],
    ideal_for: "Multi-unit residential complexes, mixed-use developments, and commercial properties.",
  },
  {
    slug: "electrical-maintenance",
    icon: Zap,
    title: "Electrical Maintenance",
    short: "Fault finding, DB board, load-shedding solutions, CoC support.",
    intro:
      "Reliable, compliant power is non-negotiable. Greenroute's electrical specialists keep DB boards safe, diagnose faults quickly, and design load-shedding solutions tailored to your estate's demands.",
    deliverables: [
      "Electrical fault finding & emergency repairs",
      "DB board maintenance, upgrades & re-labelling",
      "Inverter, battery & solar backup installations",
      "Certificate of Compliance (CoC) facilitation",
      "Common-area lighting (LED upgrades, automation)",
      "Electric fence, gate motor & access wiring",
    ],
    ideal_for: "Complexes with shared utility infrastructure and properties seeking energy resilience.",
  },
  {
    slug: "security-access-control",
    icon: ShieldCheck,
    title: "Security & Access Control",
    short: "Gate motors, intercoms, CCTV, perimeter fencing.",
    intro:
      "Safety is the foundation of community. We install, maintain and modernise the systems residents rely on every day — from gate motors to intercoms to perimeter solutions.",
    deliverables: [
      "Gate motor service, repair & replacement",
      "Intercom & visitor management system upgrades",
      "CCTV installation, repositioning and monitoring",
      "Electric fence energiser maintenance",
      "Perimeter walls, palisade & beam systems",
      "Access tag, biometric & cloud-key systems",
    ],
    ideal_for: "Estates, gated communities, and sectional title schemes prioritising resident safety.",
  },
  {
    slug: "landscaping-grounds",
    icon: Trees,
    title: "Landscaping & Groundskeeping",
    short: "Gardens, irrigation, paving, green space management.",
    image:
      "https://images.unsplash.com/photo-1766289082911-40e9eecec21d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwbGFuZHNjYXBpbmd8ZW58MHx8fHwxNzc5MzYwMTg2fDA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-2",
    intro:
      "First impressions begin at the kerb. Greenroute's landscaping teams nurture green spaces with water-wise principles and a designer's eye — keeping your estate a place residents are proud to call home.",
    deliverables: [
      "Weekly / fortnightly garden maintenance",
      "Smart irrigation design & repairs",
      "Lawn care, hedging, pruning & seasonal planting",
      "Paving, pathway & kerb maintenance",
      "Tree felling and rehabilitation",
      "Indigenous & water-wise garden conversions",
    ],
    ideal_for: "Estates that want immaculate communal grounds — sustainably maintained.",
  },
  {
    slug: "preventative-plans",
    icon: CalendarClock,
    title: "Preventative Maintenance Plans",
    short: "Scheduled inspections — catch issues before they become expensive.",
    intro:
      "The cheapest repair is the one you never need. Our preventative plans put your estate on a proactive maintenance calendar — scheduled inspections, documented checks, and predictive replacement of high-risk items.",
    deliverables: [
      "Custom annual maintenance calendar",
      "Quarterly building & systems inspections",
      "Geyser, pump & motor service intervals",
      "Roof, gutter and waterproofing health checks",
      "Compliance audits (electrical, gas, fire)",
      "Asset register & lifecycle tracking",
    ],
    ideal_for: "Trustees and managing agents seeking predictable budgets and fewer emergencies.",
  },
  {
    slug: "emergency-callout",
    icon: Siren,
    title: "Emergency Call-Out Services",
    short: "24/7 rapid response for urgent repairs and crises.",
    intro:
      "When something breaks at 2am, you need someone who answers — and shows up. Greenroute's emergency line is monitored 24/7, with prioritised dispatch for burst pipes, electrical hazards and security breaches.",
    deliverables: [
      "24/7 dispatch hotline: +27 66 183 3893",
      "Burst pipe & flood containment",
      "Electrical hazards & power outages",
      "Security breach response coordination",
      "Storm damage & emergency boarding-up",
      "Geyser failures and major leaks",
    ],
    ideal_for: "Every Greenroute-managed estate. Available as a stand-alone retainer too.",
  },
  {
    slug: "reporting-documentation",
    icon: FileCheck,
    title: "Reporting & Documentation",
    short: "Compliance logs, photos, transparent billing.",
    intro:
      "Trust is built on visibility. Every Greenroute job is documented end-to-end — work orders, photos, time logs, invoices, and compliance records — handed to trustees in a clean, audit-ready format.",
    deliverables: [
      "Per-job photo & condition reports",
      "Itemised, transparent invoicing",
      "Monthly maintenance summaries",
      "Compliance & warranty documentation",
      "AGM-ready annual reports",
      "Digital asset register (optional)",
    ],
    ideal_for: "Body corporates, audit committees, and managing agents who report to trustees.",
  },
];

export const getServiceBySlug = (slug) =>
  SERVICES.find((s) => s.slug === slug);
